import React from 'react';
import { getExpenseTheme } from './expenseGroupTheme';
import { filterActiveEmployees } from '../../utils/employeeStatusUtils';
import { ChevronDown, Wallet } from 'lucide-react';
import AppSelect from '../shared/AppSelect';

const WALLET_STYLES = {
  fixed: {
    border: 'border-indigo-200/80',
    openBg: 'open:bg-indigo-50/40',
    innerBorder: 'border-indigo-200/80',
    checkbox: 'text-indigo-700 focus:ring-indigo-600',
    field: 'focus:border-indigo-600 focus:ring-indigo-600/30',
  },
  variable: {
    border: 'border-violet-200/80',
    openBg: 'open:bg-violet-50/40',
    innerBorder: 'border-violet-200/80',
    checkbox: 'text-violet-700 focus:ring-violet-600',
    field: 'focus:border-violet-600 focus:ring-violet-600/30',
  },
};

const fieldBase =
  'w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2';
const labelCls = 'mb-1.5 block text-xs font-semibold text-gray-600 uppercase tracking-wide';

export default function ExpenseWalletSettlementSection({
  variant,
  amount,
  paymentMode,
  employees,
  recordSettleFromWallet,
  setRecordSettleFromWallet,
  selectedEmployeeId,
  setSelectedEmployeeId,
  employeeBalance,
}) {
  const theme = getExpenseTheme(variant);
  const styles = WALLET_STYLES[variant] || WALLET_STYLES.fixed;
  const activeEmployees = filterActiveEmployees(employees);
  const field = `${fieldBase} ${styles.field}`;

  const walletPreview =
    recordSettleFromWallet && selectedEmployeeId && employeeBalance && amount
      ? (() => {
          const amountNum = Number(amount) || 0;
          const mode = (paymentMode || 'Cash').toLowerCase();
          const useCash = mode === 'cash';
          const useUpi = mode === 'upi';
          const available = useCash
            ? employeeBalance.cash || 0
            : useUpi
              ? employeeBalance.upi || 0
              : employeeBalance.total || 0;
          const sufficient = available >= amountNum;
          const selectedEmp = employees?.find((emp) => emp.id === selectedEmployeeId);
          return { amountNum, useCash, useUpi, available, sufficient, selectedEmp };
        })()
      : null;

  return (
    <details
      className={`group rounded-xl border ${styles.border} bg-slate-50/60 ${styles.openBg}`}
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 text-sm font-semibold text-slate-800">
        <span className="inline-flex items-center gap-2">
          <Wallet className="h-4 w-4" />
          Settle from employee wallet
        </span>
        <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
      </summary>
      <div className={`border-t ${styles.border} px-4 pb-4 pt-3 space-y-3`}>
        <p className="text-xs text-gray-600">
          If you pay from an employee wallet, check below. Otherwise the expense is saved as{' '}
          <span className="font-semibold text-red-700">unsettled</span> and you can settle it later from
          history.
        </p>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={recordSettleFromWallet}
            onChange={(e) => {
              setRecordSettleFromWallet(e.target.checked);
              if (!e.target.checked) setSelectedEmployeeId('');
            }}
            className={`mt-1 rounded border-gray-300 ${styles.checkbox}`}
          />
          <span className="text-sm text-gray-700">
            Settle this expense from an employee wallet (deduct {theme.label.toLowerCase()} amount)
          </span>
        </label>
        {recordSettleFromWallet && (
          <div className={`rounded-lg border ${styles.innerBorder} bg-white p-4 space-y-3`}>
            <div>
              <label className={labelCls}>Wallet employee *</label>
              <AppSelect
                value={selectedEmployeeId}
                onChange={(e) => setSelectedEmployeeId(e.target.value)}
                placeholder="Select employee"
                aria-label="Wallet employee"
                sheetTitle="Wallet employee"
                options={[
                  { value: '', label: 'Select employee' },
                  ...activeEmployees.map((employee) => {
                    const balance = employee.cashBalance || { cash: 0, upi: 0 };
                    return {
                      value: employee.id,
                      label: `${employee.name} (${employee.employeeId || 'N/A'}) — Cash: ₹${balance.cash?.toLocaleString('en-IN') || 0} | UPI: ₹${balance.upi?.toLocaleString('en-IN') || 0}`,
                    };
                  }),
                ]}
                triggerClassName={`${field} min-h-[44px] flex items-center justify-between text-left touch-manipulation`}
              />
              <p className="mt-1 text-xs text-gray-500">
                Uses payment mode above: Cash or UPI balance is deducted.
              </p>
            </div>
            {walletPreview && (
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-gray-700 space-y-1">
                <p className="font-semibold text-gray-800">
                  {walletPreview.selectedEmp?.name} ({walletPreview.selectedEmp?.employeeId || 'N/A'})
                </p>
                <p>
                  Available: ₹{(employeeBalance.total || 0).toLocaleString('en-IN')} (Cash: ₹
                  {(employeeBalance.cash || 0).toLocaleString('en-IN')} | UPI: ₹
                  {(employeeBalance.upi || 0).toLocaleString('en-IN')})
                </p>
                {!walletPreview.sufficient && (
                  <p className="text-red-600 font-medium">
                    Insufficient balance for ₹{walletPreview.amountNum.toLocaleString('en-IN')} (
                    {walletPreview.useCash ? 'Cash' : walletPreview.useUpi ? 'UPI' : 'Total'})
                  </p>
                )}
                {walletPreview.sufficient && (
                  <p className="text-emerald-700 font-medium">Will be saved as settled from wallet.</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </details>
  );
}
